import type { Tarjima } from "../utils/i18n";
import type { AttendanceDayLike } from "../utils/summary";

export const HR_STATUSES = [
	"faol",
	"bloklangan",
	"tatilda",
	"betob",
	"ishdan_boshagan",
	"arxivlangan",
] as const;

export type HrStatus = (typeof HR_STATUSES)[number];

/**
 * ⚠️ Qattiq kodlangan `HR_STATUS_LABELS` lug'ati o'rniga — P7 spec §8.
 * Matn `employees.json`ning `hrStatus.*` kalitlaridan, ya'ni `t` chaqiruvchi
 * komponent orqali (`useTranslation("employees")`) uzatiladi.
 */
export function hrStatusLabel(t: Tarjima, status: HrStatus): string {
	return t(`hrStatus.${status}`);
}

export const HR_STATUS_COLORS: Record<HrStatus, string> = {
	faol: "green",
	bloklangan: "red",
	tatilda: "gold",
	betob: "volcano",
	// biome-ignore lint/style/useNamingConvention: backend hrStatus enum qiymati aynan shu
	ishdan_boshagan: "default",
	arxivlangan: "default",
};

export interface Employee {
	id: string;
	userId: string;
	fullName: string;
	idCode: string | null;
	positionId: string | null;
	departmentId: string | null;
	managerId: string | null;
	hrStatus: HrStatus;
	/**
	 * Ishga qabul qilingan sana (`YYYY-MM-DD`).
	 *
	 * ⚠️ `EmployeeSchema` da — `EmployeeDetail` da EMAS: tahrirlash formasi
	 * joriy qiymatni `create`/`update` javobidan prefill qiladi.
	 */
	hiredAt: string | null;
	/** Rasm bor-yo'qligi shu maydondan bilinadi; tarkib alohida so'raladi. */
	photoFileId: string | null;
}

/**
 * Ro'yxat elementi — `Employee` + kirish telefoni.
 *
 * ⚠️ `phone` FAQAT `GET /employees` javobida keladi (`EmployeeListItemSchema`).
 * `getById`, `create`, `update` va rasm marshrutlari `Employee` qaytaradi,
 * shuning uchun maydonni `Employee` ning o'ziga qo'shish tipni yolg'on
 * qilardi.
 */
export interface EmployeeListItem extends Employee {
	phone: string;
	/**
	 * Xavf balli va bayrog'i — kanvasdagi «Xavf» ustuni uchun.
	 *
	 * ⚠️ Bu maydonlar ro'yxatga qo'shilishi ilgari «qimmat» deb rad
	 * etilgan edi. Asos FAKTAN NOTO'G'RI ekani o'lchandi:
	 * `employees.repository.ts` ning `list()` i `getTableColumns(employees)`
	 * qiladi, ya'ni `risk_score` va `risk_flag` HOZIR HAM har sahifada
	 * DB'dan o'qilib, javob qurishda tashlab yuborilardi. Qo'shimcha narx —
	 * qator boshiga ~30 bayt JSON.
	 *
	 * ⚠️ To'rt VAQT ustuni (`riskComputedAt`, `riskAcknowledgedAt`) ro'yxatda
	 * ATAYLAB yo'q — ularni ko'rsatadigan UI faqat profilda.
	 */
	riskScore: number;
	riskFlag: boolean;
}

/** Yaratish javobi — vaqtinchalik parol shu yerda BIR MARTA keladi. */
export interface EmployeeCreatedResult {
	employee: Employee;
	temporaryPassword: string;
}

export interface CreateEmployeeInput {
	fullName: string;
	phone: string;
	idCode?: string | null;
	positionId?: string | null;
	departmentId?: string | null;
	managerId?: string | null;
	hrStatus?: HrStatus;
}

/** Telefon — faqat yaratishda. Kadr o'zgarishlari shu maydonlar orqali. */
export interface UpdateEmployeeInput {
	fullName?: string;
	idCode?: string | null;
	positionId?: string | null;
	departmentId?: string | null;
	managerId?: string | null;
	hrStatus?: HrStatus;
}

/** Forma qiymatlari — yaratish va tahrirlash bir xil maydon to'plamidan foydalanadi. */
export interface EmployeeFormValues {
	fullName: string;
	phone?: string;
	idCode?: string;
	positionId?: string;
	departmentId?: string;
	managerId?: string;
	hrStatus?: HrStatus;
}

/**
 * Xodimning eng yaqin kelgusi smenasi — profil sarlavhasidagi «Ish
 * jadvali» fakti.
 *
 * ⚠️ Manba — `shift_instances` ning eng yaqin qatori, `schedule_assignments`
 * dan QAYTA HISOBLANMAYDI. `null` — NUQSON EMAS: generator hali shu xodim
 * uchun yugurmagan yoki jadval biriktirilmagan.
 *
 * ⚠️ `startTime`/`endTime` `HH:MM:SS` shaklida keladi (Postgres `time`);
 * ko'rsatishda soniya kesiladi.
 */
export interface EmployeeSchedule {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	workDate: string;
}

/** `GET /employees/{id}` javobi (backend `EmployeeDetailSchema`). */
export interface EmployeeDetail extends Employee {
	phone: string;
	riskScore: number;
	riskFlag: boolean;
	riskComputedAt: string | null;
	riskAcknowledgedAt: string | null;
	schedule: EmployeeSchedule | null;
}

/**
 * `GET /attendance/summary` javobining bir elementi — ro'yxatdagi
 * 30 kunlik mini-lenta va profil ko'rsatkichlari uchun.
 *
 * ⚠️ `days` bo'sh bo'lishi MUMKIN va bu «ma'lumot yo'q» degani, xato emas:
 * `attendance_days` proyeksiya jadvali va uning yagona triggeri
 * `processShiftClose`.
 *
 * ⚠️ `AttendanceDayLike` `utils/summary.ts` dan IMPORT qilinadi,
 * takrorlanmaydi — ikki nusxa jimgina ajralib ketardi.
 */
export interface AttendanceSummaryItem {
	employeeId: string;
	days: AttendanceDayLike[];
}

export interface EmployeeListFilters {
	departmentId?: string;
	/**
	 * `departmentId` ning BARCHA avlodlaridagi xodimlar ham (backend
	 * `includeDescendants`). `departmentId` siz ma'nosi yo'q.
	 */
	includeDescendants?: boolean;
	positionId?: string;
	hrStatus?: HrStatus;
	q?: string;
}
